import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, User, Shield, Eye, EyeOff, Info, AlertCircle, CheckCircle } from 'lucide-react';
import { useProfileModal } from '../context/ProfileModalContext';
import { useAuth } from '../context/AuthContext';
import { authService } from '../services/authService';
import { DROPDOWN_ANIMATION, FORM_RADIUS } from './ui/tokens';
import { reportarErrorInesperado } from '../utils/reportarErrorInesperado';

function InformacionPersonalTab() {
  const { usuario } = useAuth();

  const getInitials = () => {
    const firstInitial = usuario?.nombres.charAt(0).toUpperCase() || '';
    const lastInitial = usuario?.apellidos.charAt(0).toUpperCase() || '';
    return `${firstInitial}${lastInitial}`;
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-center">
        <div className="w-24 h-24 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white text-3xl font-semibold shadow-lg">
          {getInitials()}
        </div>
      </div>

      <div className="space-y-4">
        <div className="bg-white rounded-lg p-4 border border-gray-200">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
            Nombres
          </p>
          <p className="text-base text-gray-900">
            {usuario?.nombres || '—'}
          </p>
        </div>

        <div className="bg-white rounded-lg p-4 border border-gray-200">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
            Apellidos
          </p>
          <p className="text-base text-gray-900">
            {usuario?.apellidos || '—'}
          </p>
        </div>

        <div className="bg-white rounded-lg p-4 border border-gray-200">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
            Email
          </p>
          <p className="text-base text-gray-900">
            {usuario?.email || '—'}
          </p>
        </div>

        <div className="bg-white rounded-lg p-4 border border-gray-200">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
            Teléfono
          </p>
          <p className="text-base text-gray-900">
            {usuario?.telefono || '—'}
          </p>
        </div>
      </div>

      <div className="flex items-start gap-2 pt-4 text-gray-500">
        <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
        <p className="text-sm">
          Para solicitar cambios en tus datos personales, contacta al Secretario de tu JAL
        </p>
      </div>
    </div>
  );
}

function SeguridadTab() {
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formData, setFormData] = useState({
    newPassword: '',
    confirmPassword: '',
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (formData.newPassword.length < 8) {
      setMessage({ type: 'error', text: 'La contraseña debe tener al menos 8 caracteres' });
      return;
    }

    if (formData.newPassword !== formData.confirmPassword) {
      setMessage({ type: 'error', text: 'Las contraseñas no coinciden' });
      return;
    }

    setLoading(true);
    setMessage(null);

    try {
      await authService.updatePassword(formData.newPassword);
      setMessage({ type: 'success', text: 'Contraseña actualizada correctamente' });
      setFormData({ newPassword: '', confirmPassword: '' });
    } catch (error) {
      // Quedarse sin poder cambiar la contraseña y sin que nadie lo sepa es de los
      // fallos más caros: va al toast reportable, no a un mensaje que se cierra y se olvida.
      reportarErrorInesperado(error, 'No pudimos actualizar la contraseña.');
    } finally {
      setLoading(false);
    }
  };

  const getPasswordStrength = (password: string) => {
    if (password.length === 0) return { strength: 0, label: '', color: '' };
    if (password.length < 8) return { strength: 1, label: 'Débil', color: 'bg-red-500' };

    let strength = 1;
    if (password.length >= 12) strength++;
    if (/[A-Z]/.test(password)) strength++;
    if (/[0-9]/.test(password)) strength++;
    if (/[^A-Za-z0-9]/.test(password)) strength++;

    if (strength <= 2) return { strength: 2, label: 'Media', color: 'bg-yellow-500' };
    if (strength <= 4) return { strength: 3, label: 'Buena', color: 'bg-green-500' };
    return { strength: 4, label: 'Excelente', color: 'bg-emerald-500' };
  };

  const passwordStrength = getPasswordStrength(formData.newPassword);

  return (
    <div className="space-y-6">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p className="text-sm text-blue-800 mb-2">
          <Shield className="w-4 h-4 inline mr-1" />
          Por seguridad, asegúrate de usar una contraseña fuerte y única.
        </p>
        <p className="text-xs text-blue-700 ml-5">
          La contraseña debe tener al menos 8 caracteres. Se recomienda incluir mayúsculas, números y caracteres especiales.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="profile-new-password" className="block text-sm font-medium text-slate-700 mb-1">
            Nueva Contraseña
          </label>
          <div className="relative">
            <input
              id="profile-new-password"
              type={showPassword ? 'text' : 'password'}
              value={formData.newPassword}
              onChange={(e) => setFormData({ ...formData, newPassword: e.target.value })}
              className="w-full px-4 py-2 pr-10 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
              placeholder="Mínimo 8 caracteres"
              required
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
            >
              {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
          </div>
          {formData.newPassword && (
            <div className="mt-2">
              <div className="flex gap-1 mb-1">
                {[1, 2, 3, 4].map((level) => (
                  <div
                    key={level}
                    className={`h-1 flex-1 rounded-full ${
                      level <= passwordStrength.strength ? passwordStrength.color : 'bg-slate-200'
                    }`}
                  />
                ))}
              </div>
              <p className="text-xs text-slate-700">
                Fortaleza: <span className="font-medium">{passwordStrength.label}</span>
              </p>
            </div>
          )}
        </div>

        <div>
          <label htmlFor="profile-confirm-password" className="block text-sm font-medium text-slate-700 mb-1">
            Confirmar Nueva Contraseña
          </label>
          <div className="relative">
            <input
              id="profile-confirm-password"
              type={showConfirmPassword ? 'text' : 'password'}
              value={formData.confirmPassword}
              onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
              className="w-full px-4 py-2 pr-10 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
              placeholder="Repite la contraseña"
              required
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
            >
              {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Recuadro propio y no FormErrorAlert porque también anuncia el éxito, pero con la
            misma forma que el resto: mismo radio, mismo tamaño de texto, ícono y anuncio para
            lectores de pantalla (`alert` interrumpe; `status` espera una pausa). */}
        {message && (
          <div
            role={message.type === 'success' ? 'status' : 'alert'}
            className={`flex items-start gap-2 p-3 ${FORM_RADIUS} border ${
              message.type === 'success'
                ? 'bg-green-50 text-green-800 border-green-200'
                : 'bg-red-50 text-red-700 border-red-200'
            }`}
          >
            {message.type === 'success'
              ? <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
              : <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />}
            <p className="text-sm">{message.text}</p>
          </div>
        )}

        <div className="pt-4">
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium transition-colors"
          >
            {loading ? 'Actualizando...' : 'Actualizar Contraseña'}
          </button>
        </div>
      </form>
    </div>
  );
}

function MiRolTab() {
  const { usuario } = useAuth();

  const getRoleBadgeColor = (rolNombre: string) => {
    const nombre = rolNombre.toLowerCase();
    if (nombre.includes('global')) return 'bg-purple-100 text-purple-800 border-purple-200';
    if (nombre.includes('nacional')) return 'bg-blue-100 text-blue-800 border-blue-200';
    if (nombre.includes('diocesano')) return 'bg-green-100 text-green-800 border-green-200';
    if (nombre.includes('local')) return 'bg-orange-100 text-orange-800 border-orange-200';
    return 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const getPermisos = () => {
    const rolNombre = usuario?.rol?.nombre.toLowerCase() || '';
    const permisos: string[] = [];

    if (rolNombre.includes('admin')) {
      permisos.push('Gestión de usuarios');
      permisos.push('Lectura y escritura de datos');
    } else {
      permisos.push('Solo lectura de datos');
    }

    if (rolNombre.includes('global')) {
      permisos.push('Acceso a todas las provincias');
      permisos.push('Acceso a todas las diócesis');
      permisos.push('Acceso a todos los lumisiales');
    } else if (rolNombre.includes('nacional')) {
      permisos.push('Acceso a su provincia eclesiástica');
      permisos.push('Acceso a diócesis de su provincia');
      permisos.push('Acceso a lumisiales de su provincia');
    } else if (rolNombre.includes('diocesano')) {
      permisos.push('Acceso a su diócesis');
      permisos.push('Acceso a lumisiales de su diócesis');
    } else if (rolNombre.includes('local')) {
      permisos.push('Acceso solo a su lumisial');
    }

    return permisos;
  };

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg p-6 border border-blue-100">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 rounded-full bg-blue-600 flex items-center justify-center">
            <User className="w-6 h-6 text-white" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              {usuario?.nombres} {usuario?.apellidos}
            </h3>
            <p className="text-sm text-gray-600">{usuario?.email}</p>
          </div>
        </div>

        <div className="space-y-3">
          <div>
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Rol</p>
            <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${getRoleBadgeColor(usuario?.rol?.nombre || '')}`}>
              {usuario?.rol?.nombre}
            </span>
          </div>

          {usuario?.rol?.descripcion && (
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Descripción</p>
              <p className="text-sm text-slate-700">{usuario.rol.descripcion}</p>
            </div>
          )}

          <div>
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Nivel de Acceso</p>
            <p className="text-sm text-gray-900 font-medium">Nivel {usuario?.rol?.nivel}</p>
          </div>
        </div>
      </div>

      {usuario?.lumisial && (
        <div className="bg-white rounded-lg p-4 border border-gray-200">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Lumisial Asignado</p>
          <p className="text-sm font-medium text-gray-900">{usuario.lumisial.nombre}</p>
          {usuario.lumisial.ciudad && (
            <p className="text-sm text-gray-600">{usuario.lumisial.ciudad}</p>
          )}
        </div>
      )}

      {usuario?.diocesis && (
        <div className="bg-white rounded-lg p-4 border border-gray-200">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Diócesis</p>
          <p className="text-sm font-medium text-gray-900">{usuario.diocesis.nombre}</p>
          <p className="text-xs text-gray-500 mt-1 capitalize">{usuario.diocesis.tipo}</p>
        </div>
      )}

      <div className="bg-white rounded-lg p-4 border border-gray-200">
        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-3">Permisos</p>
        <ul className="space-y-2">
          {getPermisos().map((permiso) => (
            <li key={permiso} className="flex items-start gap-2 text-sm text-slate-700">
              <div className="w-1.5 h-1.5 rounded-full bg-blue-600 mt-1.5 flex-shrink-0" />
              {permiso}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

export function ProfileModal() {
  const { isOpen, activeTab, closeProfile, setActiveTab } = useProfileModal();
  const [isClosing, setIsClosing] = useState(false);
  const isMounted = isOpen || isClosing;
  const closingGuard = useRef(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const modalRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  const finishClose = useCallback(() => {
    if (!closingGuard.current) return;
    closingGuard.current = false;
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setIsClosing(false);
    previousFocusRef.current?.focus();
    closeProfile();
  }, [closeProfile]);

  const handleClose = useCallback(() => {
    if (!isOpen || isClosing) return;
    closingGuard.current = true;
    setIsClosing(true);
    timeoutRef.current = setTimeout(finishClose, 250);
  }, [isOpen, isClosing, finishClose]);

  const handleAnimationEnd = useCallback((e: React.AnimationEvent<HTMLDivElement>) => {
    if (e.animationName === 'exit' && e.currentTarget === e.target) {
      finishClose();
    }
  }, [finishClose]);

  useEffect(() => () => { if (timeoutRef.current) clearTimeout(timeoutRef.current); }, []);

  useEffect(() => {
    if (isOpen) {
      previousFocusRef.current = document.activeElement as HTMLElement;
      modalRef.current?.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen && !isClosing) {
        handleClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, isClosing, handleClose]);

  useEffect(() => {
    if (isMounted) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isMounted]);

  if (!isMounted) return null;

  const dataState = isClosing ? 'closed' : 'open';

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  const tabs = [
    { id: 'info' as const, label: 'Información Personal' },
    { id: 'security' as const, label: 'Seguridad' },
    { id: 'role' as const, label: 'Mi Rol' },
  ];

  return createPortal(
    <div
      data-state={dataState}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:fill-mode-forwards duration-200"
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="profile-modal-title"
    >
      <div
        ref={modalRef}
        tabIndex={-1}
        data-state={dataState}
        onAnimationEnd={handleAnimationEnd}
        className={`relative w-full max-w-2xl mx-4 bg-white rounded-lg shadow-2xl max-h-[90vh] flex flex-col ${DROPDOWN_ANIMATION} data-[state=open]:duration-200 data-[state=closed]:duration-150`}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <h2 id="profile-modal-title" className="text-xl font-semibold text-gray-900">
            Mi Perfil
          </h2>
          <button
            onClick={handleClose}
            className="text-slate-400 hover:text-slate-600 transition-colors p-1 rounded-lg hover:bg-slate-100"
            aria-label="Cerrar modal"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="border-b border-slate-200">
          <nav className="flex px-6" aria-label="Pestañas de perfil">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-slate-600 hover:text-slate-900 hover:border-slate-300'
                }`}
                aria-current={activeTab === tab.id ? 'page' : undefined}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-6">
          {activeTab === 'info' && <InformacionPersonalTab />}
          {activeTab === 'security' && <SeguridadTab />}
          {activeTab === 'role' && <MiRolTab />}
        </div>
      </div>
    </div>,
    document.body
  );
}
